import React from 'react';
import DataImporter from './index';
import { Accordion, Table } from 'react-bootstrap';
import { createHierarchyStructure } from '../../api/hierarchy-structureAPI';

const DepartmentImporter = ({ onComplete }) => {
  const handleDataImport = async (rows, onProgress) => {
    let count = 0;
    for (const row of rows) {
      try {
        await createHierarchyStructure({
          name: row.name,
          parentId: row.parentId,
          code: row.code,
          functionalLocation: row.functionalLocation,
          organisationId: 1,
          sortOrder: row.sortOrder,
          billingAddressL1: row.billingAddressL1,
          billingAddressL2: row.billingAddressL2,
          billingCity: row.billingCity,
          billingState: row.billingState,
          billingPostcode: row.billingPostcode,
          billingCountry: row.billingCountry,
          billingPhone: row.billingPhone,
          deliveryAddressL1: row.deliveryAddressL1,
          deliveryAddressL2: row.deliveryAddressL2,
          deliveryCity: row.deliveryCity,
          deliveryState: row.deliveryState,
          deliveryPostCode: row.deliveryPostCode,
          deliveryCountry: row.deliveryCountry,
          deliveryPhone: row.deliveryPhone,
          isDepartment: row.isDepartment,
          shiftLogFlag: row.shiftLogFlag,
          allowEventOverlap: row.allowEventOverlap,
        });
      } catch (err) {
        console.error('Failed to import department', err);
      }
      count += 1;
      if (onProgress) onProgress(count);
    }
  };

  return (
    <>
      <div className='card h-100 p-16 rounded-0'>
        <p>
          You can bulk import <strong>departments</strong> into the system using an Excel or CSV file.
          This helps speed up onboarding or migration processes by avoiding manual entry.
        </p>
        <p>Expand the lists below to see the required and optional fields.</p>
        <div className="mb-4">
          <Accordion alwaysOpen={false}>
            <Accordion.Item eventKey="0">
              <Accordion.Header>Required Columns</Accordion.Header>
              <Accordion.Body>
                <p>Your file must include the following column headers:</p>
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Column Name</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>name</td>
                      <td>Text</td>
                      <td>Name of the department</td>
                    </tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="1">
              <Accordion.Header>Optional Columns</Accordion.Header>
              <Accordion.Body>
                <p>Your file can optionally include the following column headers:</p>
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Column Name</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>parentId</td>
                      <td>Lookup</td>
                      <td>Parent department name</td>
                    </tr>
                    <tr>
                      <td>Code</td>
                      <td>Text</td>
                      <td>Department Code</td>
                    </tr>
                    <tr>
                      <td>Functional Location</td>
                      <td>Text</td>
                      <td>Functional Location Code</td>
                    </tr>
                    <tr>
                      <td>sortOrder</td>
                      <td>Number</td>
                      <td>Sorting order value</td>
                    </tr>
                    <tr>
                      <td>billingAddressL1</td>
                      <td>Text</td>
                      <td>Billing address line 1</td>
                    </tr>
                    <tr>
                      <td>billingAddressL2</td>
                      <td>Text</td>
                      <td>Billing address line 2</td>
                    </tr>
                    <tr>
                      <td>billingCity</td>
                      <td>Text</td>
                      <td>Billing city</td>
                    </tr>
                    <tr>
                      <td>billingState</td>
                      <td>Text</td>
                      <td>Billing state</td>
                    </tr>
                    <tr>
                      <td>billingPostcode</td>
                      <td>Text</td>
                      <td>Billing postcode</td>
                    </tr>
                    <tr>
                      <td>billingCountry</td>
                      <td>Text</td>
                      <td>Billing country</td>
                    </tr>
                    <tr>
                      <td>billingPhone</td>
                      <td>Text</td>
                      <td>Billing phone</td>
                    </tr>
                    <tr>
                      <td>deliveryAddressL1</td>
                      <td>Text</td>
                      <td>Delivery address line 1</td>
                    </tr>
                    <tr>
                      <td>deliveryAddressL2</td>
                      <td>Text</td>
                      <td>Delivery address line 2</td>
                    </tr>
                    <tr>
                      <td>deliveryCity</td>
                      <td>Text</td>
                      <td>Delivery city</td>
                    </tr>
                    <tr>
                      <td>deliveryState</td>
                      <td>Text</td>
                      <td>Delivery state</td>
                    </tr>
                    <tr>
                      <td>deliveryPostCode</td>
                      <td>Text</td>
                      <td>Delivery postcode</td>
                    </tr>
                    <tr>
                      <td>deliveryCountry</td>
                      <td>Text</td>
                      <td>Delivery country</td>
                    </tr>
                    <tr>
                      <td>deliveryPhone</td>
                      <td>Text</td>
                      <td>Delivery phone</td>
                    </tr>
                    <tr>
                      <td>isDepartment</td>
                      <td>Number</td>
                      <td>1 if a department record</td>
                    </tr>
                    <tr>
                      <td>shiftLogFlag</td>
                      <td>Number</td>
                      <td>1 or 0 to indicate if the department can have a Shift Log</td>
                    </tr>
                    <tr>
                      <td>allowEventOverlap</td>
                      <td>1 or 0 to indicate if Asset Events can overlap</td>
                      <td>ID of updater</td>
                    </tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>
        <DataImporter
          entity="hierarchy-structures"
          requiredFields={[
            { key: 'name', type: 'string', required: true },
            {
              key: 'parentId',
              type: 'lookup',
              api: 'hierarchy-structures',
              nameField: 'name',
            },
            { key: 'code', type: 'text'},
            { key: 'functionalLocation', type: 'text'},
            { key: 'sortOrder', type: 'number' },
            { key: 'billingAddressL1', type: 'string' },
            { key: 'billingAddressL2', type: 'string' },
            { key: 'billingCity', type: 'string' },
            { key: 'billingState', type: 'string' },
            { key: 'billingPostcode', type: 'string' },
            { key: 'billingCountry', type: 'string' },
            { key: 'billingPhone', type: 'string' },
            { key: 'deliveryAddressL1', type: 'string' },
            { key: 'deliveryAddressL2', type: 'string' },
            { key: 'deliveryCity', type: 'string' },
            { key: 'deliveryState', type: 'string' },
            { key: 'deliveryPostCode', type: 'string' },
            { key: 'deliveryCountry', type: 'string' },
            { key: 'deliveryPhone', type: 'string' },
            { key: 'isDepartment', type: 'number' },
            { key: 'creshiftLogFlagatedBy', type: 'number' },
            { key: 'allowEventOverlap', type: 'number' },
          ]}
          onCommit={handleDataImport}
          onComplete={onComplete}
        />
      </div>
    </>
  );
};

export default DepartmentImporter;
